import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag-and-drop for board cards and rows.
//
// Two layouts share this controller. With several lists open, cards sit in
// columns, are picked up anywhere, and can move between lists. With one list
// focused, cards become rows: each is picked up by its grip and reordered in
// place, and a focused grip also moves its row with Up and Down.
//
// A positioned move is saved as the neighbours the item landed between, never
// as a computed number. The server renumbers the list in its real order with
// the item placed beside them (Paperwork::PositionedListMove), so the order
// on screen after a drop is the order a reload shows — even though the column
// is only a filtered, paginated slice of the list.
export default class extends Controller {
  static targets = ["column", "card", "unassignedColumn", "error", "announcer"]

  static values = {
    itemType: String, // 'task' or 'process'
    moveUrl: String,
  }

  // Target-connected callbacks can fire before connect(), so the registry is
  // created in initialize(), which always runs first.
  initialize() {
    this.sortables = new Map()
    this.saving = Promise.resolve()
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  disconnect() {
    this.sortables.forEach(sortable => sortable.destroy())
    this.sortables.clear()
    clearTimeout(this.errorTimer)
  }

  // Columns are lazily loaded Turbo Frames, so bind per target rather than
  // iterating once in connect() — a column may connect before or after the
  // controller does. Sortable reads its children at drag time, so binding to
  // the frame element while it still holds a skeleton is fine.
  columnTargetConnected(element) {
    this.createSortable(element)
  }

  columnTargetDisconnected(element) {
    this.destroySortable(element)
  }

  unassignedColumnTargetConnected(element) {
    this.createSortable(element)
  }

  unassignedColumnTargetDisconnected(element) {
    this.destroySortable(element)
  }

  // The board decides the layout server-side from how many lists are open, so
  // it is fixed for the life of the page.
  get rowLayout() {
    return this.element.dataset.boardDensity === "focus"
  }

  createSortable(element) {
    if (!this.sortables || this.sortables.has(element)) return

    const rows = this.rowLayout
    this.sortables.set(element, Sortable.create(element, {
      group: "kanban",
      animation: 150,
      // Dragging picks the card up off the desk: the fallback clone follows the
      // pointer wearing .paper-lifted and the original stays behind as the gap
      // it left (.paper-ghost). forceFallback is what gives us a clone to style
      // at all; the native HTML5 drag image cannot be.
      forceFallback: true,
      // The clone stays inside its column rather than moving to <body>. The row
      // layout and the density styles are scoped to the board, and a clone on
      // <body> lost them and rendered as a garbled card. It is position: fixed,
      // so the column's overflow does not clip it.
      fallbackOnBody: false,
      fallbackTolerance: 3,
      fallbackClass: "paper-lifted",
      ghostClass: "paper-ghost",
      handle: rows ? "[data-drag-handle]" : ".kanban-card",
      draggable: ".kanban-card",
      sort: !!element.dataset.listId,
      filter: "a, button:not([data-drag-handle]), input, label, [data-no-drag]",
      preventOnFilter: false,
      // A long row list scrolls inside its column; start scrolling a little
      // before the pointer reaches the edge.
      scrollSensitivity: rows ? 64 : 30,
      onStart: () => { this.dragging = true },
      onEnd: (evt) => this.dropped(evt),
    }))
  }

  destroySortable(element) {
    this.sortables?.get(element)?.destroy()
    this.sortables?.delete(element)
  }

  dropped(event) {
    this.dragging = false
    const card = event.item
    this.settle(card)

    const fromListId = event.from.dataset.listId || null
    const toListId = event.to.dataset.listId || null
    if (fromListId === toListId && event.oldIndex === event.newIndex) {
      this.flushRefresh()
      return
    }

    this.save(card, fromListId, toListId)
    this.flushRefresh()
  }

  // Up and Down on a focused grip move its row one place within the loaded
  // page. Moving past the page edge is left to dragging or sorting.
  nudge(event) {
    if (!["ArrowUp", "ArrowDown"].includes(event.key)) return
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
    event.preventDefault()

    const handle = event.currentTarget
    const card = handle.closest(".kanban-card")
    const column = card?.parentElement
    if (!card || !column?.dataset.listId || this.dragging) return

    const up = event.key === "ArrowUp"
    const neighbour = this.siblingCard(card, up ? "previousElementSibling" : "nextElementSibling")
    if (!neighbour) {
      this.announce(up ? "Already first on this page." : "Already last on this page.")
      return
    }

    if (up) {
      neighbour.before(card)
    } else {
      neighbour.after(card)
    }
    handle.focus({ preventScroll: true })
    card.scrollIntoView({ block: "nearest" })
    this.settle(card)

    const cards = this.cardsIn(column)
    this.announce(`Moved ${card.dataset.reference} to ${cards.indexOf(card) + 1} of ${cards.length}.`)
    this.save(card, column.dataset.listId, column.dataset.listId)
  }

  // Saves run one at a time, in the order they were made. Each names its
  // neighbours as they stood at that moment, so applying them in sequence
  // arrives at the order on screen however quickly moves follow each other.
  save(card, fromListId, toListId) {
    const anchors = this.anchorsFor(card)
    this.saving = this.saving.then(() => this.persist(card, fromListId, toListId, anchors))
  }

  async persist(card, fromListId, toListId, anchors) {
    const itemId = card.dataset.itemId

    try {
      const payload = await this.moveItem(itemId, fromListId, toListId, anchors)
      // Counts, aging chips and page boundaries change when an item changes
      // list; a reorder within a list changes none of them.
      if (payload.list_changed) this.refreshBoard()
    } catch (error) {
      console.error("Error moving board item:", error)
      this.recoverFromError(error.message)
    }
  }

  anchorsFor(card) {
    return {
      after_id: this.siblingCard(card, "previousElementSibling")?.dataset.itemId || null,
      before_id: this.siblingCard(card, "nextElementSibling")?.dataset.itemId || null,
    }
  }

  // The nearest card in `direction`, skipping the page marker, pagination,
  // empty-column placeholder and any drag clone that share the column.
  siblingCard(card, direction) {
    let sibling = card[direction]
    while (sibling && !(sibling.matches(".kanban-card") && !sibling.classList.contains("paper-lifted"))) {
      sibling = sibling[direction]
    }
    return sibling
  }

  cardsIn(column) {
    return Array.from(column.children).filter(child => child.matches(".kanban-card") && !child.classList.contains("paper-lifted"))
  }

  async moveItem(itemId, fromListId, toListId, placement = {}) {
    const response = await fetch(this.moveUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken,
        "Accept": "application/json",
      },
      body: JSON.stringify({
        item_type: this.itemTypeValue,
        item_id: itemId,
        from_list_id: fromListId,
        to_list_id: toListId,
        ...placement,
      }),
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      throw new Error(payload.error || "The move could not be saved.")
    }

    return response.json()
  }

  // Put the saved order back rather than leave the screen showing an order
  // that was not saved. That is a column refresh, which keeps the page, scroll
  // and selection.
  recoverFromError(message) {
    const text = message || "The move could not be saved."

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = `${text} Restoring the saved order.`
      this.errorTarget.classList.remove("hidden")
      clearTimeout(this.errorTimer)
      this.errorTimer = window.setTimeout(() => this.errorTarget.classList.add("hidden"), 5000)
    }

    this.refreshBoard()
  }

  // A refresh replaces column contents, so it waits for a drag in progress to
  // finish rather than pull the card out from under the pointer.
  refreshBoard() {
    if (this.dragging) {
      this.refreshPending = true
      return
    }
    document.dispatchEvent(new CustomEvent("board:refresh"))
  }

  flushRefresh() {
    if (!this.refreshPending) return
    this.refreshPending = false
    this.refreshBoard()
  }

  settle(card) {
    card.classList.remove("paper-settle")
    // Restart the animation when the same card settles twice in a row.
    void card.offsetWidth
    card.classList.add("paper-settle")
    card.addEventListener("animationend", () => card.classList.remove("paper-settle"), { once: true })
  }

  announce(message) {
    if (!this.hasAnnouncerTarget) return
    this.announcerTarget.textContent = ""
    window.requestAnimationFrame(() => { this.announcerTarget.textContent = message })
  }

  // Allow double-clicking a card to navigate to it
  navigateToItem(event) {
    if (event.target.closest("[data-drag-handle]")) return
    const url = event.currentTarget.dataset.url
    if (url) {
      window.location.href = url
    }
  }
}
